import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, Plus, Search, Trash2, Users } from "lucide-react";
import { listContactBatches, updateContactBatch } from "./api";
import { UploadContactBatchModal } from "./upload-contact-batch-modal";
import { useToast } from "../../components/ui/toast";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { CopyButton } from "../../components/ui/copy-button";
import { BatchRenameButton } from "./components/BatchRenameButton";

interface AutomationContactsPageProps {
  title?: string;
  uploadSource?: string;
  sourceFilter?: string;
  detailBasePath?: string;
}

export function AutomationContactsPage({ title = "Upload Contacts", uploadSource, sourceFilter, detailBasePath = "/home/automation/contacts" }: AutomationContactsPageProps) {
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ["contact-batches"],
    queryFn: listContactBatches,
  });

  const archiveMutation = useMutation({
    mutationFn: (batchId: string) => updateContactBatch(batchId, { archived: true }),
    onSuccess: () => {
      toast.success("Batch deleted");
      queryClient.invalidateQueries({ queryKey: ["contact-batches"] });
      setPendingDelete(null);
    },
    onError: (err: any) => {
      toast.danger("Failed to delete", err.message);
    }
  });

  const activeBatches = batches.filter((b: any) => {
    if (b.archived || b.source === "crm") return false;
    if (sourceFilter === "whatsapp") return b.source === "whatsapp";
    return b.source !== "whatsapp";
  });
  const needle = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!needle) return activeBatches;
    return activeBatches.filter((b: any) => {
      const name = String(b.name || "").toLowerCase();
      const id = String(b.batch_id || "").toLowerCase();
      return name.includes(needle) || id.includes(needle) || (b.tags || []).some((tag: string) => tag.toLowerCase().includes(needle));
    });
  }, [activeBatches, needle]);

  return (
    <div className="flex h-full flex-col p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-sm font-semibold text-ink">{title}</h1>

        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-ink px-3.5 py-2 text-sm font-semibold text-canvas hover:bg-ink-2"
        >
          <Plus className="h-4 w-4" /> Add Contacts
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input
            type="text"
            placeholder="Search batches by name or id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-line bg-elevated py-2 pl-9 pr-3 text-sm text-ink outline-none focus:ring-1 focus:ring-inset focus:ring-brand"
          />
        </div>
        <span className="text-xs font-semibold text-ink-3">
          Showing {filtered.length} of {activeBatches.length} batches
        </span>
      </div>

      {isLoading ? (
        <div className="p-16 text-center text-sm text-ink-3">Loading batches...</div>
      ) : activeBatches.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line bg-soft/50 py-24 text-center">
          <Users className="mb-4 h-12 w-12 text-ink-3" />
          <h3 className="mb-2 text-lg font-semibold text-ink-2">No Contact Batches</h3>
          <p className="mb-6 max-w-md text-sm text-ink-3">
            You haven't added any contacts yet. Upload a CSV/Excel file or type contacts in manually to use them in your workflows.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand/90 transition-colors"
          >
            Add your first batch
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-elevated p-16 text-center">
          <Search className="mx-auto mb-3 h-10 w-10 text-ink-3" />
          <h3 className="text-sm font-bold text-ink-2">No batches match that search</h3>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-xl border border-line bg-elevated">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-soft text-[11px] font-semibold uppercase tracking-wide text-ink-3">
              <tr>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">ID</th>
                <th className="px-4 py-2.5">Contacts</th>
                <th className="px-4 py-2.5">Created</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((batch: any) => (
                <tr
                  key={batch.batch_id}
                  onClick={() => navigate(`${detailBasePath}/${batch.batch_id}`)}
                  className="cursor-pointer border-b border-line last:border-0 hover:bg-soft/50"
                >
                  <td className="px-4 py-3">
                    <p className="font-semibold text-ink">{batch.name}</p>
                    {(batch.tags || []).length > 0 && <p className="text-xs text-brand">{batch.tags.join(" · ")}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-[11px] text-ink-3">{batch.batch_id}</span>
                      <CopyButton value={batch.batch_id} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                        {batch.valid_contacts} ready
                      </span>
                      {batch.rejected_contacts > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                          {batch.rejected_contacts} rejected
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-3">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {batch.created_at ? formatDistanceToNow(new Date(batch.created_at), { addSuffix: true }) : "Recently"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <BatchRenameButton batchId={batch.batch_id} name={batch.name} tags={batch.tags || []} />
                      <button
                        onClick={(e) => { e.stopPropagation(); setPendingDelete({ id: batch.batch_id, name: batch.name }); }}
                        disabled={archiveMutation.isPending}
                        className="rounded-lg p-2 text-ink-3 hover:bg-red-500/10 hover:text-red-500 transition-colors disabled:opacity-50"
                        title="Delete batch"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <UploadContactBatchModal onClose={() => setShowModal(false)} source={uploadSource} />}

      {pendingDelete && (
        <ConfirmModal
          title="Delete contact batch"
          body={`Delete batch "${pendingDelete.name}"? This removes it from your workflow automations.`}
          confirmLabel={archiveMutation.isPending ? "Deleting…" : "Delete batch"}
          onConfirm={() => archiveMutation.mutate(pendingDelete.id)}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
